import styled from '@emotion/styled';
import { useTheme } from '@mui/material/styles';
import { Accordion, AccordionSummary, AccordionDetails, Grid, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useDispatch } from 'react-redux';
import { getFileURL } from 'common/redux.actions';
import { Link } from 'react-router-dom';

const StyledTitle = styled(Typography)(({ theme: { typography } }) => ({
    display: 'flex',
    ...typography.h6,
    fontWeight: typography.fontWeightBold,
    whiteSpace: 'nowrap',
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme: { palette } }) => ({
    backgroundColor: palette.background.paper,
    borderBottom: `1px solid ${palette.divider}`,
    margin: 0,
    "&>div": { margin: "0 !important" }
}));

const OtherInformation = ({ expanded, onChange, panelId, title, dataInfo }) => {

    const RXDispatch = useDispatch();

    const handleLink = async (e) => {
        e.preventDefault();
        RXDispatch(getFileURL({
            handleSubmit, payload: { url: e.target.href }
        }));
    };

    const handleSubmit = (resp) => {
        if (resp.type === "success") {
            window.open(resp.url, "_blank");
        }
    };

    // Render a value cell safely: a { link } object becomes a Preview link
    // when it actually has a path, otherwise 'N/A'. Never render a raw object
    // as a React child (e.g. { link: undefined } when a document is missing).
    const renderValue = (value) => {
        if (value && typeof value === 'object') {
            return value.link
                ? <Link target='_blank' to={value.link} onClick={handleLink}> Preview</Link>
                : <Typography>N/A</Typography>;
        }
        return <Typography>{value}</Typography>;
    };

    return (
        <Accordion expanded={expanded === panelId} onChange={onChange(panelId)} sx={{ m: 0 }}>
            <StyledAccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel6bh-content"
                id="panel6bh-header"
            >
                <StyledTitle>
                    {title}
                </StyledTitle>
            </StyledAccordionSummary>
            <AccordionDetails sx={{ p: 4 }}>
                <Grid container spacing={1} sx={{ justifyContent: 'space-between' }}>
                        <Grid item xs={12}>
                        <Typography variant="h5" sx={(t) => ({ fontWeight: 'Bold', borderBottom: `1px solid ${t.palette.divider}`, marginBottom: 2, paddingBottom: .5 })}>Bank Account Details</Typography>
                    </Grid>
                    {
                        <Grid item xs={6} >
                            <Grid container>
                                <Grid item xs={10} sx={{ marginBottom: 2 }}>
                                    <Typography sx={(t) => ({ fontWeight: 'Bold', borderBottom: `1px solid ${t.palette.divider}`, marginBottom: 1, paddingBottom: .5 })}>{dataInfo?.find((info) => info.key === 'bank')?.label} </Typography>
                                    {
                                        Object.keys(dataInfo?.find((info) => info.key === 'bank')?.value).map((key) => (
                                            <Grid container key={key}>
                                                <Grid item xs={5}>
                                                    <Typography sx={{ fontWeight: "Bold" }}>{key}</Typography>
                                                </Grid>
                                                <Grid item xs={7}>
                                                    <Typography>: {dataInfo?.find((info) => info.key === 'bank')?.value[key]}</Typography>
                                                </Grid>
                                            </Grid>
                                        ))
                                    }
                                </Grid>
                            </Grid>
                        </Grid>
                    }
                        <Grid item xs={12}>
                        <Typography variant="h5" sx={(t) => ({ fontWeight: 'Bold', borderBottom: `1px solid ${t.palette.divider}`, marginBottom: 2, paddingBottom: .5 })}>Nominee Information</Typography>
                    </Grid>
                    {dataInfo?.find((info) => info.key === 'nominees')?.value?.map((info) => (
                        <Grid item xs={6} key={info.key}>
                            <Grid container>
                                <Grid item xs={10} sx={{ marginBottom: 2 }}>
                                    <Typography sx={(t) => ({ fontWeight: 'Bold', borderBottom: `1px solid ${t.palette.divider}`, marginBottom: 1, paddingBottom: .5 })}>{info?.label} </Typography>
                                    {Object.keys(info.value).map((key) => (
                                        <Grid container key={key}>
                                            <Grid item xs={5}>
                                                <Typography sx={{ fontWeight: "Bold" }}>{key}</Typography>
                                            </Grid>
                                            <Grid item xs={7}>
                                                {renderValue(info.value[key])}
                                            </Grid>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        </Grid>
                    ))}
                </Grid>
                <Grid container spacing={1} sx={{ justifyContent: 'space-between' }}>
                    <Grid item xs={12}>
                        <Typography variant="h5" sx={(t) => ({ fontWeight: 'Bold', borderBottom: `1px solid ${t.palette.divider}`, marginBottom: 2, paddingBottom: .5 })}>References</Typography>
                    </Grid>
                    {dataInfo?.find((info) => info.key === 'references')?.value?.map((info) => (
                        <Grid item xs={6} key={info.key}>
                            <Grid container>
                                <Grid item xs={10} sx={{ marginBottom: 2 }}>
                                    <Typography sx={(t) => ({ fontWeight: 'Bold', borderBottom: `1px solid ${t.palette.divider}`, marginBottom: 1, paddingBottom: .5 })}>{info?.label} </Typography>
                                    {Object.keys(info.value).map((key) => (
                                        <Grid container key={key}>
                                            <Grid item xs={5}>
                                                <Typography sx={{ fontWeight: "Bold" }}>{key}</Typography>
                                            </Grid>
                                            <Grid item xs={7}>
                                                {renderValue(info.value[key])}
                                            </Grid>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        </Grid>
                    ))}
                </Grid>
                <Grid container spacing={1} sx={{ justifyContent: 'space-between' }}>
                    <Grid item xs={12}>
                        <Typography variant="h5" sx={(t) => ({ fontWeight: 'Bold', borderBottom: `1px solid ${t.palette.divider}`, marginBottom: 2, paddingBottom: .5 })}>Insurance Information</Typography>
                    </Grid>
                    {dataInfo?.find((info) => info.key === 'insurances')?.value?.map((info) => (
                        <Grid item xs={6} key={info.key}>
                            <Grid container>
                                <Grid item xs={10} sx={{ marginBottom: 2 }}>
                                    <Typography sx={(t) => ({ fontWeight: 'Bold', borderBottom: `1px solid ${t.palette.divider}`, marginBottom: 1, paddingBottom: .5 })}>{info?.label} </Typography>
                                    {Object.keys(info.value).map((key) => (
                                        <Grid container key={key}>
                                            <Grid item xs={5}>
                                                <Typography sx={{ fontWeight: "Bold" }}>{key}</Typography>
                                            </Grid>
                                            <Grid item xs={7}>
                                                {renderValue(info.value[key])}
                                            </Grid>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        </Grid>
                    ))}
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
};

export default OtherInformation;